/**
 * Capture-timeline validator plus a single import surface for the photo-set validators.
 *
 * Re-exports the EXIF and spatial validator families so callers (and existing tests) can
 * import every photo-set check from one module. Thresholds are derived from:
 *  - Bentley ContextCapture Acquisition Best Practices
 *  - Skyfish Photogrammetry Best Practices v3.31
 *  - RMA Communication Tower Capture Guide V16.0
 */
import { FileMetadataInfo } from '../../../models/uploaded-image-metadata.model';
import { ValidationWarning, ValidationWarningType } from '../../../models/validation-warning.model';

export { validateExifMetadata, validateSetConsistency } from './exif-validators';
export { validateSpatialDistribution } from './spatial-validators';

/**
 * Max hours between first and last photo. Beyond this, sun position, shadows, and weather
 * change enough to alter surface appearance, confusing tie-point matching across the set.
 * Bentley guide: "capture on completely overcast day, around midday" -- multi-day spans violate this.
 */
const CAPTURE_TIME_SPAN_HOURS = 48;

/**
 * Flags photo sets captured over multiple days.
 * Changing sun angle and weather conditions alter shadows and surface reflectance,
 * preventing consistent tie-point matching across the set.
 */
export const validateCaptureTimeline = (photos: FileMetadataInfo[]): ValidationWarning[] => {
  const dates = photos
    .map((p) => p.exifData?.captureDate)
    .filter((d): d is Date => d instanceof Date)
    .sort((a, b) => a.getTime() - b.getTime());
  if (dates.length < 2) return [];
  const hours = (dates[dates.length - 1].getTime() - dates[0].getTime()) / 3_600_000;
  if (hours <= CAPTURE_TIME_SPAN_HOURS) return [];
  return [
    {
      type: ValidationWarningType.LARGE_CAPTURE_TIME_SPAN,
      severity: 'warning',
      message: `Photos span ${Math.round(
        hours,
      )} hours. Large time gaps cause lighting changes that degrade reconstruction.`,
    },
  ];
};
